import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '@/providers/api';
import {
  LISTADO_CLIENTES_ASIGNACION,
  LISTADO_CLIENTES_MANTENEDOR,
  LISTADO_EJECUTIVOS,
} from '@/helpers/urls';
import { ACTIVO, CARGANDO, ERROR, INACTIVO } from '../../constantes';
import RespuestaApi from '@/models/respuestas/resApi.model';
import { manejarErrorAlerta } from '@/helpers/errores';
import { Cliente } from '@/models/store/cliente.model';
import { clientesAdapter } from '@/models/adapters/cliente.adapter';
import { ClienteApi } from '@/models/respuestas/clientes.model';
import { sonObjetosIguales } from '@/helpers/objects';
import {
  CargaUtilFiltrosClientes,
  FiltrosClientes,
  SeccionFiltroEstadoCliente,
  aplicarFiltrosClientes,
  filtroRegistroClienteEstadoInicial,
  filtrosInicialesClientes,
} from './filtrosListadoClientes';
import { Ejecutivo } from '@/models/respuestas/ejecutivo.model';
import { formatDate, parseRangeToSerializable } from '@/helpers/date';
import { EJECUTIVOS_FACTORING } from '@/routes/AppRoutes';

export const obtenerListadoClientes = async (
  fechaInicio: Date,
  fechaFin: Date
) => {
  let url = '';
  const path = window.location.pathname.replace('/', '');

  if (path === EJECUTIVOS_FACTORING.ASIGNACION_EJECUTIVO) {
    url = LISTADO_CLIENTES_ASIGNACION(
      formatDate(fechaInicio),
      formatDate(fechaFin)
    );
  }

  if (path === EJECUTIVOS_FACTORING.CARPETA_CLIENTES) {
    url = LISTADO_CLIENTES_MANTENEDOR(
      formatDate(fechaInicio),
      formatDate(fechaFin)
    );
  }

  if (url) {
    const {
      data: { resultado: clientes },
    } = await api.get<RespuestaApi<ClienteApi[]>>(url);

    return clientesAdapter(clientes);
  }

  throw Error('Path incorrecto');
};

// Definición de tipos para los argumentos
interface FiltrarClientesArgs {
  fechaInicio: Date;
  fechaFin: Date;
}

export const filtrarClientesPorRegistro = createAsyncThunk(
  'clientes/filtrarPorFecha',
  async ({ fechaFin, fechaInicio }: FiltrarClientesArgs, thunk) => {
    try {
      const clientes = await obtenerListadoClientes(fechaInicio, fechaFin);
      return { clientes, rango: { from: fechaInicio, to: fechaFin } };
    } catch (error) {
      manejarErrorAlerta(
        error,
        'Hubo un error al obtener el listado de clientes'
      );

      return thunk.rejectWithValue(error);
    }
  }
);

export const crearStoreClientes = createAsyncThunk(
  'clientes/crearStore',
  async (_, thunk) => {
    try {
      const {
        data: { resultado: ejecutivos },
      } = await api.get<RespuestaApi<Ejecutivo[]>>(LISTADO_EJECUTIVOS);

      const clientes = await obtenerListadoClientes(
        filtroRegistroClienteEstadoInicial.from,
        filtroRegistroClienteEstadoInicial.to
      );

      if (clientes) {
        return {
          clientes,
          ejecutivos,
        };
      }
    } catch (error) {
      manejarErrorAlerta(
        error,
        'Hubo un error al obtener el listado de clientes'
      );

      return thunk.rejectWithValue(error);
    }
  }
);

export const limpiarFiltrosClientes = createAsyncThunk(
  'clientes/limpiarFiltros',
  async (_, thunk) => {
    try {
      return await obtenerListadoClientes(
        filtroRegistroClienteEstadoInicial.from,
        filtroRegistroClienteEstadoInicial.to
      );
    } catch (error) {
      manejarErrorAlerta(
        error,
        'Hubo un error al obtener el listado de clientes'
      );

      return thunk.rejectWithValue(error);
    }
  }
);

export type CargaUtilAgregarEjecutivoCliente = Pick<
  Cliente,
  'idCliente' | 'fechaAsignacion' | 'idEjecutivo' | 'idAsignacion'
>;

const filtrosEstadoSonIguales = (
  fActuales: SeccionFiltroEstadoCliente[],
  fIniciales: SeccionFiltroEstadoCliente[]
) => {
  if (fActuales?.length !== fIniciales?.length) return false;

  for (const fI of fIniciales) {
    // Verifico la existencia del filtro inicial dentro de los filtros seleccionados
    const fA = fActuales.find((fA) => fA.nombre === fI.nombre);

    // Si no lo encuentro termino la iteración. Los filtros no coinciden
    if (!fA) return false;

    // Luego verifico si la seleccion actual posee todas las opciones seleccionadas
    if (fI.opciones.length !== fA.opciones.length) return false;
  }

  return true;
};

const ordenarClientesPorAsignacion = (arr: Cliente[]) => {
  return arr.sort((a, b) => {
    if (a.idEjecutivo && !b.idAsignacion) {
      return 1;
    } else if (!a.idEjecutivo && b.idAsignacion) {
      return -1;
    }

    return 0;
  });
};

const estadoInicialClientes = {
  clientes: [] as Cliente[],
  clientesFiltrados: [] as Cliente[],
  ejecutivos: [] as Ejecutivo[],
  estado: INACTIVO,
  filtrado: false,
  filtrosIniciales: filtrosInicialesClientes,
  filtrosActuales: filtrosInicialesClientes,
};

const sonFiltrosPredeterminados = (
  nuevosFiltros: FiltrosClientes,
  filtrosIniciales: FiltrosClientes
) => {
  return (
    nuevosFiltros.cliente === filtrosIniciales.cliente &&
    sonObjetosIguales(nuevosFiltros.ejecutivo, filtrosIniciales.ejecutivo) &&
    filtrosEstadoSonIguales(
      nuevosFiltros.estado,
      JSON.parse(JSON.stringify(filtrosIniciales.estado))
    ) &&
    sonObjetosIguales(nuevosFiltros.registro, filtrosIniciales.registro)
  );
};

const clientesSlice = createSlice({
  name: 'clientes',
  initialState: { ...estadoInicialClientes },
  reducers: {
    filtrarClientes: (
      state,
      { payload }: PayloadAction<CargaUtilFiltrosClientes>
    ) => {
      if (!state.clientes) return;

      const { filtrosIniciales, clientes: c, filtrosActuales } = state;
      const { carga, filtro } = payload;

      const nuevosFiltros: FiltrosClientes = JSON.parse(
        JSON.stringify({ ...filtrosActuales, [filtro]: carga })
      );

      let nuevosClientes = JSON.parse(JSON.stringify(c));

      if (sonFiltrosPredeterminados(nuevosFiltros, filtrosIniciales)) {
        state.filtrado = false;
      } else {
        state.filtrado = true;
        nuevosClientes = aplicarFiltrosClientes(nuevosClientes, nuevosFiltros);
      }

      state.clientesFiltrados = nuevosClientes;
      state.filtrosActuales = { ...nuevosFiltros };
    },
    agregarEjecutivoCliente: (
      state,
      { payload }: PayloadAction<CargaUtilAgregarEjecutivoCliente>
    ) => {
      const { idCliente } = payload;

      const indexCliente = state.clientes.findIndex(
        (c) => c.idCliente === idCliente
      );
      const indexClienteFiltrado = state.clientesFiltrados.findIndex(
        (c) => c.idCliente === idCliente
      );

      if (indexCliente > -1) {
        const cliente = state.clientes[indexCliente];

        const clienteActualizado = {
          ...cliente,
          ...payload,
        };

        if (indexCliente > -1) {
          state.clientes[indexCliente] = {
            ...clienteActualizado,
          };
        }

        if (indexClienteFiltrado > -1) {
          state.clientesFiltrados[indexClienteFiltrado] = {
            ...clienteActualizado,
          };
        }
      }

      state.clientes = ordenarClientesPorAsignacion(state.clientes);
      state.clientesFiltrados = ordenarClientesPorAsignacion(
        state.clientesFiltrados
      );
    },
  },
  extraReducers: (builder) => {
    builder
      //#region Iniciar store
      .addCase(crearStoreClientes.pending, (state) => ({
        ...state,
        estado: CARGANDO,
      }))
      .addCase(crearStoreClientes.rejected, (state) => ({
        ...state,
        estado: ERROR,
      }))
      .addCase(
        crearStoreClientes.fulfilled,
        (state, { payload: { clientes, ejecutivos } }) => {
          const filtrosIniciales: FiltrosClientes = {
            ...state.filtrosIniciales,
            ejecutivo: ejecutivos
              ? [...ejecutivos.map((e) => e.idEjecutivo)]
              : [],
          };

          return {
            ...state,
            estado: ACTIVO,
            clientes,
            ejecutivos,
            clientesFiltrados: clientes,
            filtrosActuales: filtrosIniciales,
            filtrosIniciales,
          };
        }
      );
    //#endregion
    //#region Filtro por fechas
    builder
      .addCase(filtrarClientesPorRegistro.pending, (state) => ({
        ...state,
        estado: CARGANDO,
      }))
      .addCase(
        filtrarClientesPorRegistro.fulfilled,
        (state, { payload: { clientes, rango } }) => {
          const nuevosFiltros: FiltrosClientes = JSON.parse(
            JSON.stringify({
              ...state.filtrosActuales,
              registro: parseRangeToSerializable(rango),
            })
          );

          if (
            sonFiltrosPredeterminados(nuevosFiltros, state.filtrosIniciales)
          ) {
            state.filtrado = false;
          } else {
            state.filtrado = true;
            state.clientesFiltrados = aplicarFiltrosClientes(
              clientes,
              state.filtrosActuales
            );
          }

          state.filtrosActuales = { ...nuevosFiltros };
          state.clientes = clientes;
          state.estado = ACTIVO;
        }
      );
    //#endregion
    //#region Limpieza de filtros
    builder
      .addCase(limpiarFiltrosClientes.pending, (state) => ({
        ...state,
        estado: CARGANDO,
      }))
      .addCase(
        limpiarFiltrosClientes.fulfilled,
        (state, { payload: clientes }) => ({
          ...state,
          estado: ACTIVO,
          filtrosActuales: state.filtrosIniciales,
          filtrado: false,
          clientes,
          clientesFiltrados: clientes,
        })
      );
    //#endregion
  },
});

export const { filtrarClientes, agregarEjecutivoCliente } =
  clientesSlice.actions;
export default clientesSlice.reducer;
